// buildRelationalEngine.js
// Motor relacional VITA — Detección automática desde narrativas
// Las relaciones NO se declaran — se detectan desde convergencia narrativa

export function buildRelationalEngine(processedActors = []) {
  if (processedActors.length < 2) {
    return processedActors.map(actor => ({
      ...actor,
      _vita: {
        ...actor._vita,
        relacional: buildEmptyRelacional()
      }
    }));
  }

  const grafo = buildGrafoNarrativo(processedActors);

  return processedActors.map(actor => {
    const relacional = buildActorRelacional(actor.id, grafo);
    return {
      ...actor,
      _vita: {
        ...actor._vita,
        relacional
      }
    };
  });
}

function buildGrafoNarrativo(actors) {
  const grafo = {};

  for (const actor of actors) {
    // Recopilar todas las narrativas del actor — array nuevo + narrativaDominante
    const narrativasArray = Array.isArray(actor.narrativas)
      ? actor.narrativas.map(n => typeof n === 'string' ? normalize(n) : normalize(n?.texto || '')).filter(Boolean)
      : [];

    const narrativaDominante = normalize(
      actor.narrativaDominante ||
      actor._vita?.reasoning?.narrativaDominante ||
      ''
    );

    if (narrativaDominante && !narrativasArray.includes(narrativaDominante)) {
      narrativasArray.push(narrativaDominante);
    }

    grafo[actor.id] = {
      id: actor.id,
      nombre: actor.nombre || '',
      conexiones: [],
      IVT: actor._vita?.scoring?.IVT || 0,
      estado: actor._vita?.state?.state || 'Estable',
      narrativa: narrativaDominante,
      narrativas: narrativasArray,
      foco: normalize(actor.foco),
      riesgo: normalize(actor.riesgo),
      estado_actor: normalize(actor.estado)
    };
  }

  const actorList = Object.values(grafo);

  for (let i = 0; i < actorList.length; i++) {
    for (let j = i + 1; j < actorList.length; j++) {
      const a = actorList[i];
      const b = actorList[j];

      const relacion = detectarRelacion(a, b);

      if (relacion) {
        grafo[a.id].conexiones.push({
          targetId: b.id,
          ...relacion
        });
        grafo[b.id].conexiones.push({
          targetId: a.id,
          ...relacion
        });
      }
    }
  }

  return grafo;
}

function detectarRelacion(a, b) {
  let puntaje = 0;
  const factores = [];

  // Narrativa compartida — detectar desde array completo de narrativas
  const narrativasA = a.narrativas || [];
  const narrativasB = b.narrativas || [];

  const narrativasCompartidas = narrativasA.filter(n =>
    n &&
    n !== 'narrativa territorial en observación' &&
    n !== 'sin narrativa declarada' &&
    narrativasB.includes(n)
  );

  if (narrativasCompartidas.length > 0) {
    puntaje += 5 + (narrativasCompartidas.length - 1) * 2;
    factores.push('narrativa_compartida');
  }

  // Foco compartido — refuerza pero no es suficiente solo
  if (a.foco && b.foco && a.foco === b.foco) {
    puntaje += 2;
    factores.push('foco_compartido');
  }

  // Sin narrativa compartida no hay relación
  if (!factores.includes('narrativa_compartida')) return null;

  const tipo = inferirTipo(a, b, factores);
  const intensidad = puntaje >= 6 ? 'Alta' : puntaje >= 5 ? 'Media' : 'Baja';

  return {
    tipo,
    intensidad,
    puntaje,
    factores,
    origen: 'automatico'
  };
}

function inferirTipo(a, b, factores) {
  const estadoA = a.estado_actor;
  const estadoB = b.estado_actor;
  const riesgoA = a.riesgo;
  const riesgoB = b.riesgo;

  const aEsNegativo = estadoA === 'crítico' || riesgoA === 'crítico' || riesgoA === 'alto';
  const bEsNegativo = estadoB === 'crítico' || riesgoB === 'crítico' || riesgoB === 'alto';
  const aEsPositivo = estadoA === 'positivo' || estadoA === 'favorable';
  const bEsPositivo = estadoB === 'positivo' || estadoB === 'favorable';

  if (aEsNegativo && bEsNegativo) return 'Convergencia';
  if (aEsPositivo && bEsPositivo) return 'Aliado';
  if ((aEsNegativo && bEsPositivo) || (aEsPositivo && bEsNegativo)) return 'Tensión';
  if (factores.includes('narrativa_compartida')) return 'Coordinación';

  return 'Neutro';
}

function buildActorRelacional(actorId, grafo) {
  const nodo = grafo[actorId];
  if (!nodo) return buildEmptyRelacional();

  const conexiones = nodo.conexiones;
  const grado = conexiones.length;
  const totalActores = Object.keys(grafo).length;

  const centralidad = totalActores > 1
    ? Math.round((grado / (totalActores - 1)) * 100)
    : 0;

  const nivelCentralidad = (() => {
    if (centralidad >= 70) return 'Alta';
    if (centralidad >= 40) return 'Media';
    if (centralidad >= 15) return 'Baja';
    return 'Aislado';
  })();

  const aliados = conexiones.filter(c =>
    c.tipo === 'Aliado' || c.tipo === 'Coordinación'
  ).length;

  const opositores = conexiones.filter(c =>
    c.tipo === 'Convergencia' || c.tipo === 'Tensión'
  ).length;

  const esPuente = detectarPuente(actorId, grafo);
  const clusterId = detectarCluster(actorId, grafo);

  const actoresConectados = conexiones.map(c => {
    const nodoConectado = grafo[c.targetId];
    return {
      actorId: c.targetId,
      nombre: nodoConectado?.nombre || '',
      tipo: c.tipo,
      intensidad: c.intensidad,
      factores: c.factores || [],
      IVT: nodoConectado?.IVT || 0,
      estado: nodoConectado?.estado || 'Estable'
    };
  }).sort((a, b) => b.IVT - a.IVT);

  const presionRelacional = actoresConectados.length
    ? Math.round(
        actoresConectados.reduce((sum, a) => sum + a.IVT, 0) /
        actoresConectados.length
      )
    : 0;

  const convergentes = actoresConectados.filter(
    a => a.tipo === 'Convergencia'
  );

  return {
    grado,
    centralidad,
    nivelCentralidad,
    esPuente,
    clusterId,
    aliados,
    opositores,
    presionRelacional,
    actoresConectados,
    convergentes,
    totalConexiones: grado
  };
}

function detectarPuente(actorId, grafo) {
  const nodo = grafo[actorId];
  if (!nodo || nodo.conexiones.length < 2) return false;

  const vecinos = nodo.conexiones.map(c => c.targetId);

  for (let i = 0; i < vecinos.length; i++) {
    for (let j = i + 1; j < vecinos.length; j++) {
      const conectados = grafo[vecinos[i]]?.conexiones.some(
        c => c.targetId === vecinos[j]
      );
      if (!conectados) return true;
    }
  }

  return false;
}

function detectarCluster(actorId, grafo) {
  const visitados = new Set();
  const cola = [actorId];
  visitados.add(actorId);

  while (cola.length > 0) {
    const actual = cola.shift();
    const conexiones = grafo[actual]?.conexiones || [];

    for (const conexion of conexiones) {
      if (!visitados.has(conexion.targetId)) {
        visitados.add(conexion.targetId);
        cola.push(conexion.targetId);
      }
    }
  }

  return [...visitados].sort()[0];
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function buildEmptyRelacional() {
  return {
    grado: 0,
    centralidad: 0,
    nivelCentralidad: 'Aislado',
    esPuente: false,
    clusterId: null,
    aliados: 0,
    opositores: 0,
    presionRelacional: 0,
    actoresConectados: [],
    convergentes: [],
    totalConexiones: 0
  };
}